'use client';

import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import EventCard from '@sympla/sympla-components/EventCard';
import { SearchEvent } from 'searchEvent.d';
import { ParamsResult } from 'ParamsResult';
import useLocation from 'hooks/useLocation';
import { createPropsToTrackingMatrix } from 'components/Integration/createPropsToTrackingMatrix';
import { filterParams } from 'components/SearchFilter/filterOptions';
import {
  SearchListStyle,
  SearchGridColumns,
} from 'components/SearchList/SearchList.css';
import {
  cardTypeTrackingSearch,
  componentTrackingSearch,
  typeTrackingSearch,
} from 'constants/search';
import { MATRIX_TYPE } from 'constants/index';

interface SearchEventListProps {
  events: SearchEvent[];
  serviceParams?: ParamsResult;
  isMobile: boolean;
  tracking?: {
    label?: string;
    prefix?: string;
    cardType?: string;
    matrizTemplate?: string;
  };
}

const CAMPAIGN_PARAMS = [
  'd',
  'account_id',
  'timestamp',
  'hash',
  'utm_source',
  'utm_campaign',
] as const;

function SearchEventList({
  events,
  serviceParams,
  isMobile,
  tracking,
}: SearchEventListProps) {
  const locationStore = useLocation();
  const searchParams = useSearchParams();

  const filters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filterParams).map(([key, content]) => {
        const queryParam = searchParams?.get(content.name) as string | undefined;
        return queryParam ? [key, queryParam] : [key, false];
      }),
    );
  }, [searchParams]);

  const componentTemplate = useMemo(() => {
    if (tracking?.label) return tracking.label;
    if (serviceParams?.url_params?.component?.slug) {
      return serviceParams.url_params.component.slug;
    }
    if (serviceParams?.search_params?.q) return componentTrackingSearch.term;
    return '';
  }, [tracking?.label, serviceParams?.url_params?.component?.slug, serviceParams?.search_params?.q]);

  const addCampaignParams = useMemo(() => {
    const campaignParams = CAMPAIGN_PARAMS.reduce((acc, param) => {
      const value = searchParams?.get(param);
      if (value) {
        acc.push(`${param}=${encodeURIComponent(value)}`);
      }
      return acc;
    }, [] as string[]);

    if (campaignParams.length === 0) {
      return (url: string) => url;
    }

    const paramsString = campaignParams.join('&');

    return (url: string) => {
      if (!url) return url;
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${paramsString}`;
    };
  }, [searchParams]);

  return (
    <div
      className={`${SearchListStyle} ${SearchGridColumns.eventList}`}
      data-testid="search-eventlist-container"
    >
      {events?.map((event, idx) => (
        <EventCard
          key={event.id}
          hasPriority={!idx}
          {...event}
          {...createPropsToTrackingMatrix(idx, {
            type: typeTrackingSearch.event,
            trackingPrefix: tracking?.prefix || MATRIX_TYPE.SEARCH,
            matrizTemplate: tracking?.matrizTemplate || '',
            componentTemplate,
            dataLocation: locationStore.selectedLocation,
            cardType: tracking?.cardType || cardTypeTrackingSearch.eventCardSearch,
            serviceParams,
            payloadCard: event,
            filters,
          })}
          fullWidth={isMobile}
          layoutCard="lateral-on-mobile"
          data-testid={`search-eventlist-card-${event.id}`}
          url={addCampaignParams(event?.url)}
        />
      ))}
    </div>
  );
}

export default observer(SearchEventList);
